/**
 * Mass Messages → Drafts: opgeslagen concepten bekijken, bewerken, verwijderen
 * en goedkeuren.
 *
 * 04-10-2026 (Jordy): "Ik wil Mass Messages als concept kunnen opslaan, zodat
 * ik ze later kan goedkeuren en dan alsnog laten versturen." Een concept komt
 * uit de tab Create ("Save as draft") en wordt nooit verstuurd: de server
 * bewaart het met status `draft`, en de verzendlus pakt alleen `scheduled` op.
 *
 * Per concept: model (foto), platform, methode, begin van de tekst, content
 * (miniaturen), prijs of gratis, de bedoelde tijd, wie het maakte en wanneer.
 * Wat niet meer klopt (model weg of niet actief, content weg of gearchiveerd)
 * staat er in het rood bij; de server rekent dat uit (`problems`).
 *
 *   - Edit: hetzelfde formulier als een slot in Create (`MassDmVelden`), met
 *     dezelfde controles; de tijd mag hier nog leeg of voorbij zijn.
 *   - Delete: na bevestiging.
 *   - Approve (alleen admin en owner, ook op de server): eerst dezelfde
 *     controle als "Schedule all" (dry run, met de rechten van wie goedkeurt).
 *     Is de bedoelde tijd voorbij, dan eerst een nieuwe tijd (standaard nu,
 *     naar boven op 5 minuten). Dan de bevestiging met model, methode, tijd,
 *     prijs en doelgroep, bij echte fans met SEND. Daarna staat het in de
 *     Agenda en is het concept weg.
 *
 * 07-10-2026 (Jordy): tijden in de tijdzone van de gebruiker (lib/tijdzone.ts,
 * standaard Amsterdam), met de zone erbij; invoer wordt in die zone gelezen.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Field, Modal } from '../components/Modal'
import { ModelFilter, useModelFilter } from '../components/ModelFilter'
import { ModelNaam } from '../components/ModelNaam'
import { Empty, ErrorNote, Loading, Plat, Seg, Tag } from '../components/ui'
import {
  MassDmVelden, leegMassDm, massDmAfgeleid, massDmFouten, methodeNaam, modelKeuzes,
  type MassDmWaarden, type Methode,
} from '../components/MassDmFormulier'
import { useApi } from '../lib/useApi'
import { useActions } from '../lib/actions'
import { api, ApiError, thumbUrl } from '../lib/api'
import { alsZone, metZone, metZoneEnAmsterdam, zoneDelen, zoneLabel, zoneNaarUtc } from '../lib/amsterdam'
import { useMijnZone } from '../lib/tijdzone'
import { prijsPerFanTekst } from '../lib/massPrijs'
import { standaardMoment, tijdFout } from '../lib/tijdkeuze'
import { TijdKiezer } from '../components/TijdKiezer'
import type { Persona } from './Models'
import { EnRegel } from '../components/EnRegel'

const CONCEPTEN = '/api/admin/conversations/mass-message/drafts'
const GEPLAND = '/api/admin/conversations/mass-message/scheduled'

/** Eén concept zoals `GET mass-message/drafts` hem teruggeeft. */
export interface Concept {
  id: number
  platform: string
  model_slug: string | null
  model_name: string | null
  content: string | null
  has_media: boolean
  content_ids: number[]
  content_items: { id: number; title: string; content_type: string | null; base_price?: number | null; missing: boolean }[]
  is_paid: boolean
  price: number
  only_test: boolean
  exclude_test: boolean
  fc_delivery_mode: string
  exclude_recent_minutes: number
  exclude_fan_active_minutes?: number
  exclude_timewasters: boolean
  exclude_maybe_timewasters?: boolean
  audience_include: string | null
  exclude_spenders: boolean
  exclude_new_followers: boolean
  exclude_private_chats: boolean
  online_only: boolean
  translation_enabled: boolean
  translate_to: string | null
  scheduled_at: string | null
  created_by_name: string
  created_at: string | null
  problems: string[]
}

/** Het concept terug in het formulier van een slot. */
export function waardenUitConcept(c: Concept): MassDmWaarden {
  const platform = c.platform === 'f2f' ? 'f2f' : 'fancentro'
  const items = (c.content_items ?? []).filter((it) => !it.missing)
  return {
    ...leegMassDm((c.fc_delivery_mode === 'native' ? 'native' : 'direct') as Methode),
    modelSleutel: c.model_slug ? `${platform}:${c.model_slug}` : '',
    publiek: c.only_test ? 'test' : 'echt',
    tekst: c.content ?? '',
    vertaal: Boolean(c.translation_enabled),
    doelgroep: c.audience_include || 'all',
    recent: Number(c.exclude_recent_minutes || 0),
    fanActief: Number(c.exclude_fan_active_minutes || 0),
    geenTimewasters: Boolean(c.exclude_timewasters),
    geenMaybe: Boolean(c.exclude_maybe_timewasters),
    geenBetalers: Boolean(c.exclude_spenders),
    geenNieuwe: Boolean(c.exclude_new_followers),
    geenPrive: Boolean(c.exclude_private_chats),
    alleenOnline: Boolean(c.online_only),
    media: items.length
      ? {
          items: items.map((it) => ({ id: it.id, title: it.title, content_type: it.content_type, base_price: it.base_price })),
          betaald: Boolean(c.is_paid),
          prijs: c.is_paid ? String(c.price) : '',
        }
      : null,
    // 05-10-2026: keuze en prijs los van de content (ook een concept zonder content).
    betaald: Boolean(c.is_paid),
    prijs: c.is_paid ? String(c.price) : '',
  }
}

const DOELGROEP: Record<string, string> = {
  all: 'All addressable fans', spenders: 'Paying fans only', new_followers: 'New followers only',
  timewasters: 'Timewasters only', waiting: 'Waiting queue only', new_subscriptions: 'New subscriptions only',
}

/** Wat er in de bevestiging per concept staat. */
interface Keuring {
  c: Concept
  stap: 'tijd' | 'bevestig'
  datum: string
  tijd: string
  /** De gekozen nieuwe tijd (UTC), of null = de bedoelde tijd van het concept. */
  nieuw: string | null
  moment: string | null
  getypt: string
}

export function MassaConcepten({
  magDirect, magNative, magBetaald, magGoedkeuren,
}: {
  magDirect: boolean
  magNative: boolean
  magBetaald: boolean
  /** Owner en admin; de server weigert de rest ook. */
  magGoedkeuren: boolean
}) {
  const q = useApi<{ items: Concept[]; count: number; can_approve: boolean }>(CONCEPTEN)
  const personas = useApi<Persona[]>('/api/admin/models/personas')
  const keuzes = useMemo(() => modelKeuzes(personas.data ?? []), [personas.data])
  // 04-10-2026 (Jordy, punt D): modelfoto en naam via het gedeelde ModelNaam
  // van [algemeen] (lib/modellen), geen eigen versie.
  const { run, busy, notify } = useActions()
  const [bewerk, setBewerk] = useState<Concept | null>(null)
  const [keuring, setKeuring] = useState<Keuring | null>(null)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState('')
  // 04-10-2026: het gedeelde modelfilter van [algemeen] (?model=…, FC + F2F samen).
  const filter = useModelFilter()
  const zone = useMijnZone()

  const goedkeuren = magGoedkeuren && (q.data?.can_approve ?? true)

  /** Stap 1 en 2: controleren (dry run); bij een voorbije tijd eerst een nieuwe tijd. */
  async function controleer(c: Concept, nieuw: string | null, basis?: Keuring) {
    setBezig(true)
    setFout('')
    try {
      const uit = await api.post<{ item: { scheduled_at: string } }>(
        `${CONCEPTEN}/${c.id}/approve`, { dry_run: true, ...(nieuw ? { scheduled_at: nieuw } : {}) })
      const m = standaardMoment(undefined, zone)
      setKeuring({
        c, stap: 'bevestig', datum: basis?.datum ?? m.datum, tijd: basis?.tijd ?? m.tijd,
        nieuw, moment: uit.item.scheduled_at, getypt: '',
      })
    } catch (e) {
      const data = e instanceof ApiError ? (e.data as { code?: string } | null) : null
      if (data?.code === 'time_in_past') {
        const m = standaardMoment(undefined, zone)
        setKeuring({ c, stap: 'tijd', datum: m.datum, tijd: m.tijd, nieuw: null, moment: null, getypt: '' })
        setFout('')
      } else {
        setFout(e instanceof Error ? e.message : String(e))
        if (!basis) notify(e instanceof Error ? e.message : String(e), false)
      }
    } finally {
      setBezig(false)
    }
  }

  async function keurGoed(k: Keuring) {
    setBezig(true)
    try {
      const r = await api.post<{ item?: { status?: string } }>(
        `${CONCEPTEN}/${k.c.id}/approve`, k.nieuw ? { scheduled_at: k.nieuw } : {})
      setKeuring(null)
      // 12-10-2026: een automatisch concept wordt "approved" en pas met Send approved ingepland.
      notify(r?.item?.status === 'approved'
        ? `Approved: the automatic Mass Message for ${k.c.model_name || k.c.model_slug} is not scheduled yet. `
          + 'An owner schedules it with Send approved on the Auto tab.'
        : `Approved: the Mass Message for ${k.c.model_name || k.c.model_slug} is scheduled and in the Agenda.`)
      q.refetch()
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    } finally {
      setBezig(false)
    }
  }

  if (q.loading && !q.data) return <Loading />
  if (q.error) return <ErrorNote error={q.error} />
  const alle = q.data?.items ?? []
  const lijst = alle.filter((c) => filter.past(c.model_slug))

  return (
    <div className="massa-concepten">
      <div className="filters">
        <ModelFilter filter={filter} />
        <span className="mut2" style={{ fontSize: 12.5 }} data-testid="concept-telling">
          {lijst.length === alle.length ? `${alle.length} drafts` : `${lijst.length} of ${alle.length} drafts`}
        </span>
      </div>
      <div className="mut2" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Drafts are never sent. Save them from the Create tab with “Save as draft”.
        {goedkeuren
          ? ' Approve schedules a draft like “Schedule all” does; it then moves to the Agenda.'
          : ' An admin or owner approves them; until then nothing goes out.'}
      </div>
      {!lijst.length && alle.length ? (
        <Empty>No drafts for this model.</Empty>
      ) : !lijst.length ? (
        <Empty>
          No drafts. <Link to="/mass-messages?tab=create">Create a Mass Message</Link> and choose “Save as draft”.
        </Empty>
      ) : (
        <div className="concept-lijst">
          {lijst.map((c) => {
            const items = c.content_items ?? []
            const prijs = prijsPerFanTekst(c.platform, c.is_paid, c.price, items.length)
            return (
              <div key={c.id} className={c.problems.length ? 'card concept invalid' : 'card concept'}
                   data-testid="concept">
                <div className="concept-kop">
                  <div className="concept-naam">
                    <ModelNaam slug={c.model_slug} naam={c.model_name} size={30} vet />{' '}<Plat value={c.platform} />
                    <div className="mut2" style={{ fontSize: 12 }}>
                      {methodeNaam(c.fc_delivery_mode === 'native' ? 'native' : 'direct')}
                      {' · '}{c.only_test ? 'Test accounts' : DOELGROEP[c.audience_include || 'all'] ?? 'Real fans'}
                    </div>
                  </div>
                  {items.length ? (
                    <span className={c.is_paid ? 'tag acc' : 'tag mut'} data-testid="concept-prijs">{prijs}</span>
                  ) : <span className="tag mut" data-testid="concept-prijs">Free · text only</span>}
                  {c.only_test ? <Tag kind="mut">Test accounts</Tag> : <Tag kind="warn">Real fans</Tag>}
                </div>
                <div className="concept-tekst">{(c.content || '').trim() || <span className="mut">(no text)</span>}</div>
                <EnRegel tekst={c.content} />
                {items.length ? (
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {items.map((it) => it.missing
                      ? <span key={it.id} className="agenda-thumb concept-weg" title="Removed from the library">✕</span>
                      : <img key={it.id} className="agenda-thumb" src={thumbUrl(it.id)} alt={it.title} loading="lazy" />)}
                  </div>
                ) : null}
                <div className="concept-meta mut2">
                  <span>Intended: <span className="mono">{c.scheduled_at ? metZone(c.scheduled_at, zone) : '—'}</span></span>
                  <span>By {c.created_by_name || '—'} · <span className="mono">{metZone(c.created_at, zone)}</span></span>
                </div>
                {c.problems.length ? (
                  <ul className="massa-slot-errors" role="alert" data-testid="concept-problemen">
                    {c.problems.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                ) : null}
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button className="btn sm" type="button" onClick={() => setBewerk(c)}>Open / edit</button>
                  <button className="btn ghost sm" type="button" disabled={busy !== null}
                          onClick={() => run({
                            label: 'Delete draft',
                            confirm: `Delete this draft for ${c.model_name || c.model_slug}? It was never sent and this can't be undone.`,
                            request: () => api.del(`${CONCEPTEN}/${c.id}`),
                            successText: 'Draft deleted.',
                            onDone: q.refetch,
                          })}>
                    Delete
                  </button>
                  {goedkeuren ? (
                    <button className="btn primary sm" type="button" disabled={bezig || c.problems.length > 0}
                            title={c.problems.length ? 'Fix the problems first (Open / edit).' : undefined}
                            onClick={() => { void controleer(c, null) }}>
                      Approve…
                    </button>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {bewerk ? (
        <ConceptBewerken c={bewerk} keuzes={keuzes} magDirect={magDirect} magNative={magNative}
                         magBetaald={magBetaald} zone={zone}
                         onSluit={() => setBewerk(null)}
                         onOpgeslagen={() => { setBewerk(null); q.refetch() }} />
      ) : null}

      {keuring?.stap === 'tijd' ? (
        <Modal title="The intended time has passed" width={460} onClose={() => setKeuring(null)}
               desc={`This draft was meant for ${metZone(keuring.c.scheduled_at, zone)}. Choose a new time.`}
               footer={
                 <>
                   <button className="btn ghost sm" type="button" onClick={() => setKeuring(null)}>Cancel</button>
                   <button className="btn primary sm" type="button"
                           disabled={bezig || Boolean(tijdFout(keuring.datum, keuring.tijd, undefined, zone))}
                           onClick={() => {
                             const m = zoneNaarUtc(keuring.datum, keuring.tijd, zone)
                             if (m) void controleer(keuring.c, m, keuring)
                           }}>
                     {bezig ? 'Checking…' : 'Continue'}
                   </button>
                 </>
               }>
          <TijdKiezer datum={keuring.datum} tijd={keuring.tijd} naam="New" label={`New date and time (${zoneLabel(zone)})`}
                      zone={zone} onChange={(m) => setKeuring({ ...keuring, ...m })} />
          {tijdFout(keuring.datum, keuring.tijd, undefined, zone)
            ? <div className="massa-slot-errors">{tijdFout(keuring.datum, keuring.tijd, undefined, zone)}</div> : null}
          {fout ? <div className="massa-slot-errors" role="alert">{fout}</div> : null}
        </Modal>
      ) : null}

      {keuring?.stap === 'bevestig' ? (() => {
        const c = keuring.c
        const echteFans = !c.only_test
        const items = c.content_items ?? []
        const mag = !bezig && (!echteFans || keuring.getypt.trim().toUpperCase() === 'SEND')
        return (
          <Modal title="Approve and schedule this Mass Message?" width={560} onClose={() => setKeuring(null)}
                 desc="It goes out on its own at the time shown. You can still cancel it in the Agenda until then."
                 footer={
                   <>
                     <button className="btn ghost sm" type="button" onClick={() => setKeuring(null)}>Cancel</button>
                     <button className="btn primary sm" type="button" disabled={!mag}
                             onClick={() => { void keurGoed(keuring) }}>
                       {bezig ? 'Scheduling…' : 'Approve and schedule'}
                     </button>
                   </>
                 }>
            <div className="concept-bevestig" data-testid="concept-bevestig">
              <span>Model</span>
              <span className="row" style={{ gap: 6 }}>
                <ModelNaam slug={c.model_slug} naam={c.model_name} size={20} vet /> <Plat value={c.platform} />
              </span>
              <span>Method</span><span>{methodeNaam(c.fc_delivery_mode === 'native' ? 'native' : 'direct')}</span>
              <span>When</span><span className="mono">{metZoneEnAmsterdam(keuring.moment, zone)}</span>
              <span>Price</span><span>{prijsPerFanTekst(c.platform, c.is_paid, c.price, items.length)}</span>
              <span>Audience</span>
              <span>{echteFans ? <Tag kind="warn">Real fans</Tag> : <Tag kind="mut">Test accounts</Tag>}
                {' '}{DOELGROEP[c.audience_include || 'all'] ?? ''}</span>
              <span>Draft by</span><span>{c.created_by_name || '—'}</span>
            </div>
            {echteFans ? (
              <Field label="Type SEND to confirm" hint="This Mass Message goes to real fans.">
                <input className="inp" value={keuring.getypt} placeholder="SEND"
                       onChange={(e) => setKeuring({ ...keuring, getypt: e.target.value })} />
              </Field>
            ) : null}
            {fout ? <div className="massa-slot-errors" role="alert">{fout}</div> : null}
          </Modal>
        )
      })() : null}
    </div>
  )
}

/** Een concept openen en bewerken: het formulier van een slot, plus de bedoelde tijd.
 *
 *  11-10-2026 (Jordy): ook voor een automatisch concept (tab Auto, `auto`): hetzelfde
 *  volledige formulier om er handmatig een PPV van te maken (content uit de
 *  bibliotheek van dit model + Free/Paid met prijs). Model, platform, methode en
 *  tijd liggen dan vast; Paid zonder content wordt al bij opslaan geweigerd. */
export function ConceptBewerken({
  c, keuzes, magDirect, magNative, magBetaald, zone, onSluit, onOpgeslagen, auto = false, gepland = false,
}: {
  c: Concept
  zone: string
  keuzes: ReturnType<typeof modelKeuzes>
  magDirect: boolean
  magNative: boolean
  magBetaald: boolean
  onSluit: () => void
  onOpgeslagen: () => void
  auto?: boolean
  /** 16-10-2026 (Jordy): een GEPLAND Mass Message aanpassen (Overview → Scheduled).
   *  Ander endpoint, de tijd is ook bij een automatisch bericht te wijzigen, en
   *  opslaan vraagt één bevestiging: het verandert wat er naar fans gaat. */
  gepland?: boolean
}) {
  const [w, setW] = useState<MassDmWaarden>(() => waardenUitConcept(c))
  // 07-10-2026: in de zone van de gebruiker; blijven datum en tijd zoals ze
  // waren, dan gaat precies het oude moment terug (er verschuift niets).
  const vanConcept = zoneDelen(c.scheduled_at, zone)
  const begin = vanConcept ?? standaardMoment(undefined, zone)
  const [datum, setDatum] = useState(begin.datum)
  const [tijd, setTijd] = useState(begin.tijd)
  const [fout, setFout] = useState('')
  const [bezig, setBezig] = useState(false)
  const { notify } = useActions()
  const a = massDmAfgeleid(w, keuzes, magBetaald)
  // 05-10-2026: een concept mag "Paid unlock" zonder content bewaren; een
  // automatisch concept niet (11-10-2026, Jordy: bij opslaan al weigeren).
  const meldingen = massDmFouten(w, a, { concept: !auto })
  const [getalBevestigd, setGetalBevestigd] = useState(false)
  const getalInTekst = auto && /\d|[€$£]|\b(ppv|credits?)\b/i.test(w.tekst)
  // 05-10-2026 (Jordy): Platform Mass DM is voor iedereen de standaard
  // (nieuw slot, Clear, kopiëren, nieuw concept).
  const standaard: Methode = magNative ? 'native' : 'direct'
  const zet = (patch: Partial<MassDmWaarden>) => setW((oud) => ({ ...oud, ...patch }))

  async function bewaar() {
    if (meldingen.length) return
    const tijdOngewijzigd = Boolean(vanConcept && c.scheduled_at && datum === vanConcept.datum && tijd === vanConcept.tijd)
    const nieuwMoment = (auto && !gepland) || tijdOngewijzigd ? (c.scheduled_at ?? '') : (zoneNaarUtc(datum, tijd, zone) ?? '')
    if (gepland && !window.confirm(
      `Change this scheduled Mass Message? It goes out ${alsZone(nieuwMoment, zone)} (${zoneLabel(zone)})`
      + `${tijdOngewijzigd ? ' (same time as before)' : ' (NEW time)'} with what is shown here.`)) return
    setBezig(true)
    setFout('')
    try {
      await api.patch(gepland ? `${GEPLAND}/${c.id}` : `${CONCEPTEN}/${c.id}`, {
        ...a.basis,
        content_ids: a.metMedia ? (w.media?.items ?? []).map((it) => it.id) : [],
        is_paid: a.gekozenBetaald,
        price: a.gekozenBetaald ? a.prijsGetal : 0,
        // 11-10-2026: bij een automatisch concept blijft de tijd van de planner
        // (een gepland bericht mag wel een nieuwe tijd krijgen, 16-10-2026).
        scheduled_at: nieuwMoment,
        ...(auto ? { number_confirmed: getalBevestigd } : {}),
      })
      notify(gepland ? 'Scheduled Mass Message saved.' : 'Draft saved.')
      onOpgeslagen()
    } catch (e) {
      const data = e instanceof ApiError ? (e.data as { errors?: { detail: string }[] } | null) : null
      setFout(data?.errors?.length ? data.errors.map((x) => x.detail).join(' ') : (e instanceof Error ? e.message : String(e)))
    } finally {
      setBezig(false)
    }
  }

  return (
    <Modal title={`${gepland ? 'Scheduled Mass Message' : auto ? 'Automatic draft' : 'Draft'} · ${c.model_name || c.model_slug || ''}`} width={640} onClose={onSluit}
           desc={gepland
             ? `Scheduled for ${metZone(c.scheduled_at, zone)}. A change here changes what goes out to fans. It is locked 3 minutes before sending.`
             : `Saved by ${c.created_by_name || '—'} on ${metZone(c.created_at, zone)}. Nothing is sent from here.`}
           footer={
             <>
               <button className="btn ghost sm" type="button" onClick={onSluit}>Close</button>
               <button className="btn primary sm" type="button" data-testid="draft-save"
                       disabled={bezig || meldingen.length > 0 || (getalInTekst && !getalBevestigd)}
                       onClick={() => { void bewaar() }}>
                 {bezig ? 'Saving…' : gepland ? 'Save changes' : 'Save draft'}
               </button>
             </>
           }>
      {c.problems.length ? (
        <ul className="massa-slot-errors" role="alert" data-testid="concept-problemen" style={{ marginTop: 0, marginBottom: 12 }}>
          {c.problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      ) : null}
      <Field label="Method">
        {magDirect && magNative && !auto ? (
          <Seg options={[
                 { value: 'direct', label: 'Dashboard Mass Message' },
                 { value: 'native', label: 'Platform Mass DM' },
               ]}
               value={w.methode ?? standaard}
               onChange={(v) => zet({ methode: v as Methode })} />
        ) : <span>{methodeNaam(auto ? (w.methode ?? standaard) : standaard)}</span>}
      </Field>
      <MassDmVelden w={w} zet={zet} aiSuggesties keuzes={keuzes} magBetaald={magBetaald}
                    echteFansHint={gepland ? 'Real fans. This goes out at the time below.'
                      : 'Real fans. SEND is asked when an admin approves this draft.'} />
      {gepland ? (
        <>
          <TijdKiezer datum={datum} tijd={tijd} naam="Scheduled" label={`Send at (${zoneLabel(zone)})`} zone={zone}
                      hint="Leave as is to keep the sending time. A new time must be at least 2 minutes ahead."
                      onChange={(m) => { setDatum(m.datum); setTijd(m.tijd) }} />
          {getalInTekst ? (
            <label className="row" style={{ gap: 6 }}>
              <input type="checkbox" checked={getalBevestigd} onChange={(e) => setGetalBevestigd(e.target.checked)} />
              <span>The text contains a number or price on purpose (it is logged). The price field does not count.</span>
            </label>
          ) : null}
        </>
      ) : auto ? (
        <>
          <Field label="Planned time">
            <span data-testid="auto-planned-time">{metZone(c.scheduled_at, zone)} (set by the planner)</span>
          </Field>
          {getalInTekst ? (
            <label className="row" style={{ gap: 6 }}>
              <input type="checkbox" checked={getalBevestigd} onChange={(e) => setGetalBevestigd(e.target.checked)} />
              <span>The text contains a number or price on purpose (it is logged). The price field does not count.</span>
            </label>
          ) : null}
        </>
      ) : (
        <TijdKiezer datum={datum} tijd={tijd} naam="Draft" label={`Intended date and time (${zoneLabel(zone)})`}
                    alleenToekomst={false} zone={zone}
                    hint="Pick a time from the list or use the buttons. May be past for a draft; a new time is asked when it is approved."
                    onChange={(m) => { setDatum(m.datum); setTijd(m.tijd) }} />
      )}
      {meldingen.length || fout ? (
        <ul className="massa-slot-errors" role="alert">
          {meldingen.map((m) => <li key={m}>{m}</li>)}
          {fout ? <li>{fout}</li> : null}
        </ul>
      ) : null}
    </Modal>
  )
}
